import React from 'react';
import styled, { keyframes } from 'styled-components';
import { LockKeyhole, ShieldCheck } from 'lucide-react';

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const floatSignal = keyframes`
  0%, 100% { opacity: .28; transform: translateY(0) scale(.85); }
  50% { opacity: 1; transform: translateY(-7px) scale(1.15); }
`;

export const PageContainer = styled.main`
  position: relative;
  display: grid;
  min-height: 100vh;
  place-items: center;
  overflow: hidden;
  padding: clamp(18px, 4vw, 54px);
  color: #101218;
  font-family: "Plus Jakarta Sans", Inter, Arial, sans-serif;
  background-color: #f6f8fb;
  background-image:
    linear-gradient(rgba(16, 18, 24, .038) 1px, transparent 1px),
    linear-gradient(90deg, rgba(16, 18, 24, .038) 1px, transparent 1px);
  background-size: 36px 36px;
  box-sizing: border-box;

  &::before {
    position: absolute;
    top: 50%;
    left: 25%;
    width: min(760px, 80vw);
    height: min(760px, 80vw);
    content: "";
    background: radial-gradient(circle, rgba(17, 92, 255, .18), rgba(17, 92, 255, .04) 45%, transparent 72%);
    border-radius: 50%;
    filter: blur(12px);
    transform: translate(-50%, -50%);
    pointer-events: none;
  }

  * { box-sizing: border-box; }
`;

export const PaymentCard = styled.section`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(245px, .74fr) minmax(420px, 1.26fr);
  width: min(960px, 100%);
  max-width: 960px;
  min-width: 0;
  min-height: 650px;
  overflow: hidden;
  background: rgba(255, 255, 255, .94);
  border: 1px solid rgba(16, 18, 24, .13);
  border-radius: 4px;
  box-shadow: 0 34px 90px rgba(27, 42, 72, .18);
  backdrop-filter: blur(18px);

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    width: 100%;
    max-width: 100%;
    min-height: 0;
  }
`;

export const CheckoutPanel = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  width: 100%;
  overflow: hidden;
  padding: clamp(30px, 5vw, 58px);

  @media (max-width: 480px) {
    padding: 28px 16px 24px;
  }
`;

const StoryPanel = styled.aside`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  padding: 34px 30px 30px;
  color: white;
  background:
    radial-gradient(circle at 48% 45%, rgba(37, 101, 255, .34), transparent 31%),
    linear-gradient(rgba(255,255,255,.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,.035) 1px, transparent 1px),
    #0e1320;
  background-size: auto, 34px 34px, 34px 34px, auto;

  &::after {
    position: absolute;
    right: -120px;
    bottom: -110px;
    width: 310px;
    height: 310px;
    content: "";
    border: 1px dashed rgba(83, 132, 255, .34);
    border-radius: 50%;
    box-shadow: 0 0 0 30px rgba(17,92,255,.035), 0 0 0 75px rgba(17,92,255,.025);
  }

  @media (max-width: 720px) {
    min-height: 205px;
    padding: 25px 23px 22px;
  }
`;

const Brand = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  gap: 11px;
  align-items: center;

  img { width: 52px; height: 39px; object-fit: contain; filter: brightness(0) invert(1); }
  strong { display: block; font-size: 11px; letter-spacing: .14em; }
  small { display: block; margin-top: 4px; color: #8eaeff; font: 700 6px/1 monospace; letter-spacing: .12em; }
`;

const StoryCopy = styled.div`
  position: relative;
  z-index: 2;
  margin: auto 0;

  > span { color: #79a3ff; font: 700 7px/1 monospace; letter-spacing: .14em; text-transform: uppercase; }
  h2 { max-width: 265px; margin: 18px 0 14px; color: white; font-size: clamp(31px, 3.1vw, 45px); font-weight: 720; line-height: .96; letter-spacing: -.055em; }
  p { max-width: 275px; margin: 0; color: rgba(255,255,255,.57); font-size: 10px; line-height: 1.7; }

  @media (max-width: 720px) {
    margin: 34px 0 0;
    h2 { max-width: 420px; margin: 10px 0 0; font-size: 28px; }
    p { display: none; }
  }
`;

const SignalMap = styled.div`
  position: absolute;
  top: 50%;
  right: -45px;
  width: 220px;
  height: 220px;
  border: 1px solid rgba(67, 120, 255, .25);
  border-radius: 50%;
  transform: translateY(-50%);

  &::before, &::after { position: absolute; content: ""; border: 1px dashed rgba(67,120,255,.23); border-radius: 50%; }
  &::before { inset: 28px; }
  &::after { inset: 63px; border-color: rgba(255,106,43,.35); }
  i { position: absolute; width: 5px; height: 5px; background: #2c6bff; border-radius: 50%; box-shadow: 0 0 11px #2c6bff; animation: ${floatSignal} 3s ease-in-out infinite; }
  i:nth-child(1) { top: 15%; left: 35%; }
  i:nth-child(2) { top: 49%; left: 8%; background:#ff6a2b; box-shadow:0 0 11px #ff6a2b; animation-delay:-1s; }
  i:nth-child(3) { right: 23%; bottom: 13%; animation-delay:-2s; }

  @media (max-width: 720px) { top: 58%; right: -40px; width: 180px; height: 180px; }
`;

const StoryFooter = styled.div`
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: center;
  padding-top: 18px;
  color: rgba(255,255,255,.52);
  font: 700 6px/1 monospace;
  letter-spacing: .1em;
  text-transform: uppercase;
  border-top: 1px solid rgba(255,255,255,.13);

  strong { display: flex; gap: 6px; align-items: center; color: #79a3ff; }
`;

export function PaymentBrandPanel({ gateway = 'PhonePe', eyebrow = 'Secure checkout' }) {
  return (
    <StoryPanel>
      <Brand>
        <img src="/willovate-mark.png" alt="" />
        <div><strong>WILLOVATE</strong><small>PAYMENT RUNTIME</small></div>
      </Brand>
      <SignalMap aria-hidden="true"><i /><i /><i /></SignalMap>
      <StoryCopy>
        <span>{eyebrow}</span>
        <h2>One clear step.<br />Protected end to end.</h2>
        <p>Your order stays connected while the selected payment partner securely completes the transaction.</p>
      </StoryCopy>
      <StoryFooter>
        <span>Gateway / {gateway}</span>
        <strong><ShieldCheck size={13} /> Encrypted</strong>
      </StoryFooter>
    </StoryPanel>
  );
}

export const Header = styled.header`
  margin-bottom: 27px;
  text-align: left;

  > span { display: inline-flex; gap: 7px; align-items: center; color: #115cff; font: 700 7px/1 monospace; letter-spacing: .12em; text-transform: uppercase; }
  > span::before { width: 6px; height: 6px; content: ""; background: #115cff; border-radius: 50%; box-shadow: 0 0 9px rgba(17,92,255,.65); }
`;

export const Title = styled.h1`
  margin: 12px 0 7px;
  color: #101218;
  font-size: clamp(28px, 3vw, 38px);
  font-weight: 720;
  line-height: 1;
  letter-spacing: -.05em;
`;

export const Subtitle = styled.p`
  margin: 0;
  color: #737b88;
  font-size: 11px;
  line-height: 1.55;
`;

export const SummaryBox = styled.div`
  position: relative;
  min-width: 0;
  margin-bottom: 23px;
  padding: 21px 22px;
  background: #f6f8fb;
  border: 1px solid #e1e5ec;
  border-radius: 3px;
`;

export const AmountRow = styled.div`
  display: flex;
  gap: 18px;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 17px;
  padding-bottom: 18px;
  border-bottom: 1px solid #dfe3e9;

  @media (max-width: 480px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 9px;
  }
`;

export const AmountLabel = styled.span`
  display: block;
  padding-bottom: 5px;
  color: #777f8c;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .06em;
  text-transform: uppercase;
`;

export const Amount = styled.span`
  display: block;
  color: #115cff;
  font-size: clamp(31px, 4vw, 44px);
  font-weight: 760;
  line-height: 1;
  letter-spacing: -.055em;
`;

export const DetailRow = styled.div`
  display: grid;
  grid-template-columns: minmax(90px, .7fr) minmax(0, 1.3fr);
  gap: 15px;
  align-items: center;
  min-height: 31px;
  border-bottom: 1px solid rgba(16,18,24,.065);
  &:last-child { border-bottom: 0; }
`;

export const DetailLabel = styled.span`
  color: #7b828f;
  font-size: 10px;
`;

export const DetailValue = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
  color: #20242d;
  font-size: 11px;
  font-weight: 700;
  text-align: right;
`;

export const AlertBox = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-bottom: 20px;
  padding: 13px 15px;
  color: ${props => props.$success ? '#137a4c' : '#a22e38'};
  font-size: 10px;
  line-height: 1.55;
  background: ${props => props.$success ? '#edfbf4' : '#fff1f2'};
  border: 1px solid ${props => props.$success ? '#bcebd2' : '#f1c7cb'};
  border-radius: 3px;
  svg { flex: 0 0 auto; }
`;

export const Button = styled.button`
  display: flex;
  gap: 9px;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 54px;
  padding: 0 18px;
  font: inherit;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .04em;
  border: 0;
  border-radius: 2px;
  cursor: pointer;
  transition: transform 180ms ease, box-shadow 180ms ease, background 180ms ease;

  &:disabled { opacity: .5; cursor: not-allowed; }
`;

export const PrimaryButton = styled(Button)`
  margin-bottom: 10px;
  color: white;
  background: linear-gradient(105deg, #101827, #115cff 72%, #3e7aff);
  box-shadow: 0 10px 28px rgba(17,92,255,.2);
  &:hover:not(:disabled) { box-shadow: 0 14px 34px rgba(17,92,255,.3); transform: translateY(-2px); }
`;

export const SecondaryButton = styled(Button)`
  color: #5f6673;
  background: #f2f4f7;
  border: 1px solid #e1e5eb;
  &:hover:not(:disabled) { color: #101218; background: #e9edf3; }
`;

export const Footer = styled.footer`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  align-items: center;
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid #e1e5eb;

  @media (max-width: 430px) { grid-template-columns: 1fr; }
`;

export const FooterText = styled.p`
  display: flex;
  gap: 7px;
  align-items: center;
  margin: 0;
  color: #858c98;
  font-size: 8px;

  &::before { width: 5px; height: 5px; content: ""; background: #16a267; border-radius: 50%; box-shadow: 0 0 7px rgba(22,162,103,.45); }
`;

export const PoweredBy = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  color: #7a818d;
  font-size: 8px;
  strong { color: #101218 !important; letter-spacing: .03em; }
`;

export const LoadingSpinner = styled.div`
  width: 62px;
  height: 62px;
  margin: 0 auto 25px;
  background: conic-gradient(from 0deg, #115cff, #115cff 16%, #e1e7f2 17%, #e1e7f2);
  border: 7px solid white;
  border-radius: 50%;
  box-shadow: 0 0 0 1px #d7dde8, 0 0 30px rgba(17,92,255,.18);
  animation: ${spin} .9s linear infinite;
`;

export const StatusWrap = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 420px;
  text-align: center;
`;

export const StatusIcon = styled.div`
  display: grid;
  width: 84px;
  height: 84px;
  margin: 0 auto 25px;
  place-items: center;
  font-size: 34px;
  font-weight: 800;
  border-radius: 50%;
  box-shadow: 0 0 0 10px currentColor;

  &.success { color:#159764; background:#e8f8f1; box-shadow:0 0 0 10px rgba(21,151,100,.08),0 0 30px rgba(21,151,100,.15); }
  &.failed, &.error { color:#d8434f; background:#fff0f1; box-shadow:0 0 0 10px rgba(216,67,79,.07),0 0 30px rgba(216,67,79,.12); }
  &.pending { color:#d48a16; background:#fff7e7; box-shadow:0 0 0 10px rgba(212,138,22,.08),0 0 30px rgba(212,138,22,.12); }
`;

export const StatusTitle = styled.h1`
  margin: 0 0 10px;
  color: #101218;
  font-size: 30px;
  font-weight: 720;
  letter-spacing: -.045em;
`;

export const StatusMessage = styled.p`
  max-width: 390px;
  margin: 0 auto 24px;
  color: #707784;
  font-size: 11px;
  line-height: 1.65;
`;

export const StatusDetails = styled(SummaryBox)`
  width: 100%;
  margin-top: 2px;
  text-align: left;
`;

export const SecureMark = ({ children }) => (
  <FooterText><LockKeyhole size={12} />{children}</FooterText>
);
